package fr.frux.feature.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.HelpOutline
import androidx.compose.material.icons.automirrored.outlined.OpenInNew
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material.icons.outlined.Code
import androidx.compose.material.icons.outlined.Description
import androidx.compose.material.icons.outlined.HealthAndSafety
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.MailOutline
import androidx.compose.material.icons.outlined.NotificationsNone
import androidx.compose.material.icons.outlined.PrivacyTip
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import fr.frux.app.resources.Res
import fr.frux.app.resources.profile_row_ad_choices
import fr.frux.app.resources.profile_row_ad_choices_subtitle
import fr.frux.app.resources.settings_row_contact
import fr.frux.app.resources.settings_row_help
import fr.frux.app.resources.settings_row_legal_notice
import fr.frux.app.resources.settings_row_notifications
import fr.frux.app.resources.settings_row_privacy
import fr.frux.app.resources.settings_row_safety
import fr.frux.app.resources.settings_row_showcase
import fr.frux.app.resources.settings_row_terms
import fr.frux.app.resources.settings_section_legal
import fr.frux.app.resources.settings_title
import fr.frux.app.resources.settings_version_label
import fr.frux.core.ads.LocalAdsController
import fr.frux.core.common.config.SiteLinks
import fr.frux.core.common.config.appVersion
import fr.frux.core.common.config.isDevBuild
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.theme.FruxSpacing
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

/**
 * Ligne de navigation générique (icône, libellé, indicateur), factorisée entre les entrées
 * ci-dessous. [external] remplace le chevron par l'icône de sortie : la ligne ouvre le navigateur
 * ou la messagerie, pas un écran de l'app.
 */
@Composable
private fun SettingsNavRow(
    icon: ImageVector,
    label: String,
    onClick: () -> Unit,
    external: Boolean = false,
    subtitle: String? = null,
) {
    FruxCard(
        modifier = Modifier.fillMaxWidth(),
        onClick = onClick,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.width(FruxSpacing.md))
                Column {
                    Text(
                        text = label,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    if (subtitle != null) {
                        Text(
                            text = subtitle,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }
            Icon(
                imageVector = if (external) Icons.AutoMirrored.Outlined.OpenInNew else Icons.Outlined.ChevronRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun SettingsVersionRow() {
    FruxCard(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                text = stringResource(Res.string.settings_version_label),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = appVersion(),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun SettingsSectionTitle(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.titleSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier =
            Modifier
                .padding(horizontal = FruxSpacing.xs, vertical = FruxSpacing.sm)
                .semantics { heading() },
    )
}

/**
 * Réglages, sous-page sans bottom nav.
 *
 * Première vraie préférence (#225 P0) : les canaux/catégories de notification, sur son propre
 * écran (toggles multiples) plutôt qu'inlinés ici.
 *
 * Ouvert sans compte depuis #468 : Play et le RGPD veulent les pages légales atteignables avant
 * toute inscription. [showAccountSettings] masque alors ce qui n'a de sens qu'avec un compte.
 */
@Composable
fun SettingsScreen(
    onBack: () -> Unit,
    onOpenShowcase: () -> Unit,
    onOpenNotificationPreferences: () -> Unit,
    showAccountSettings: Boolean = true,
) {
    val uriHandler = LocalUriHandler.current
    Scaffold(
        topBar = {
            FruxTopBar(title = stringResource(Res.string.settings_title), onBack = onBack)
        },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(paddingValues)
                    .verticalScroll(rememberScrollState())
                    .padding(FruxSpacing.md),
        ) {
            if (showAccountSettings) {
                SettingsNavRow(
                    icon = Icons.Outlined.NotificationsNone,
                    label = stringResource(Res.string.settings_row_notifications),
                    onClick = onOpenNotificationPreferences,
                )

                Spacer(modifier = Modifier.height(FruxSpacing.md))
            }

            SettingsNavRow(
                icon = Icons.Outlined.HealthAndSafety,
                label = stringResource(Res.string.settings_row_safety),
                onClick = { uriHandler.openUri(SiteLinks.SAFETY) },
                external = true,
            )

            Spacer(modifier = Modifier.height(FruxSpacing.md))

            SettingsSectionTitle(stringResource(Res.string.settings_section_legal))
            LegalRows(openUri = uriHandler::openUri)
            AdChoicesRow()

            Spacer(modifier = Modifier.height(FruxSpacing.md))

            // Outil de dev (#336) : jamais en release Android ni sur le web, cf. isDevBuild().
            if (isDevBuild()) {
                SettingsNavRow(
                    icon = Icons.Outlined.Code,
                    label = stringResource(Res.string.settings_row_showcase),
                    onClick = onOpenShowcase,
                )

                Spacer(modifier = Modifier.height(FruxSpacing.md))
            }

            SettingsVersionRow()
        }
    }
}

/**
 * Section « Aide et informations » : le centre d'aide en tête (#484), puis les trois pages légales
 * du site dans l'ordre de son pied (#468), et le contact. Aucune ligne n'exige de compte.
 */
@Composable
private fun LegalRows(openUri: (String) -> Unit) {
    val rows =
        listOf(
            Triple(
                Icons.AutoMirrored.Outlined.HelpOutline,
                stringResource(Res.string.settings_row_help),
                SiteLinks.HELP,
            ),
            Triple(Icons.Outlined.Description, stringResource(Res.string.settings_row_terms), SiteLinks.TERMS),
            Triple(Icons.Outlined.PrivacyTip, stringResource(Res.string.settings_row_privacy), SiteLinks.PRIVACY),
            Triple(Icons.Outlined.Info, stringResource(Res.string.settings_row_legal_notice), SiteLinks.LEGAL_NOTICE),
            Triple(
                Icons.Outlined.MailOutline,
                stringResource(Res.string.settings_row_contact),
                SiteLinks.CONTACT_MAILTO,
            ),
        )
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        rows.forEach { (icon, label, url) ->
            SettingsNavRow(
                icon = icon,
                label = label,
                onClick = { openUri(url) },
                external = true,
            )
        }
    }
}

/**
 * « Gérer mes choix publicitaires » (ADR 0030, #527) : même condition que dans le Profil. Cette
 * page est atteignable sans compte, or UMP exige un accès permanent aux choix et retirer son
 * consentement doit être aussi simple que le donner.
 */
@Composable
private fun AdChoicesRow() {
    val adsController = LocalAdsController.current
    val adsState by adsController.state.collectAsState()
    val scope = rememberCoroutineScope()
    if (adsState.supported && adsState.privacyOptionsAvailable) {
        Spacer(modifier = Modifier.height(FruxSpacing.sm))
        SettingsNavRow(
            icon = Icons.Outlined.PrivacyTip,
            label = stringResource(Res.string.profile_row_ad_choices),
            subtitle = stringResource(Res.string.profile_row_ad_choices_subtitle),
            onClick = { scope.launch { adsController.openPrivacyOptions() } },
        )
    }
}
